import { useLockedPositionAPY, type LockedPositionStore, useDialogPortal } from "@/hooks";
import { memo, useMemo, type MouseEvent, useCallback } from "react";
import { usePortfolioPositionRow } from "../usePortfolioPositionRow";
import { PositionTableRow } from "../row/PositionTableRow";
import type { PositionStatus } from "../row/position-table-row.utils";
import { usePortfolioStore } from "@/zustand/PortfolioStore";
import type { LockMoreFormDialogProps } from "@/components/dialogs";
import { TOKEN_IDS } from "@/types";
import { formatDisplayCurrency } from "@/utils/format-currency";

export interface LockedPositionRowProps {
    position: LockedPositionStore;
    index: number;
    isMenuOpen: boolean;
    menuIndex: number | null;
    onMenuToggle: (
        event: MouseEvent<HTMLElement>,
        position: LockedPositionStore,
        index: number,
        status?: PositionStatus,
    ) => void;
    status?: PositionStatus;
}

export const LockedPositionRow = memo(function LockedPositionRow({
    position,
    index,
    isMenuOpen,
    menuIndex,
    onMenuToggle,
    status,
}: LockedPositionRowProps) {
    // const { address } = useAccount();
    // const { data } = useProtocolPNL(address);

    const { durationText } = usePortfolioPositionRow({
        position,
        positionType: "locked",
    });

    const { selectedPosition, setSelectedPosition } = usePortfolioStore();
    const rowKey = `locked-${index}`;
    const isSelected = selectedPosition?.key === rowKey;
    const handleSelect = useCallback(() => {
        setSelectedPosition({ type: "locked", key: rowKey, position });
    }, [position, rowKey, setSelectedPosition]);

    const { openDialog } = useDialogPortal();

    const lockDurationWeeks = useMemo(() => Number(position.lockDuration.split("w")[0]), [position.lockDuration]);

    const handleAction = useCallback(() => {
        openDialog({
            dialogType: "LOCK_MORE_FORM",
            dialogProps: {
                existingDuration: lockDurationWeeks,
                initialTokenId: TOKEN_IDS.IUSD,
            } satisfies LockMoreFormDialogProps,
        });
    }, [openDialog, position, lockDurationWeeks]);

    const { apy, isAPYLoading } = useLockedPositionAPY(lockDurationWeeks);

    return (
        <PositionTableRow
            durationText={durationText}
            principal={`${formatDisplayCurrency(position.lockedBalance ?? 0)} iUSD`}
            rewards={formatDisplayCurrency(position.rewardsEarned ?? 0)}
            apy={isAPYLoading ? "—" : apy ? `${apy}%` : "N/A"}
            isActionDisabled={false}
            handleAction={handleAction}
            actionButtonText="Lock More"
            isMenuOpen={isMenuOpen}
            menuIndex={menuIndex}
            index={index}
            onMenuToggle={(event) => onMenuToggle(event, position, index)}
            status={status}
            isSelected={isSelected}
            onSelect={handleSelect}
        />
    );
});

LockedPositionRow.displayName = "LockedPositionRow";
